import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../i18n/LanguageContext';
const TYPE_SPEED_MIN = 45;
const TYPE_SPEED_MAX = 70;
const ERASE_SPEED_MIN = 25;
const ERASE_SPEED_MAX = 40;
const PAUSE_AFTER_TYPE = 750;
const PAUSE_AFTER_ERASE = 280;
function randomBetween(a: number, b: number) {
  return Math.floor(Math.random() * (b - a + 1)) + a;
}
export function HeroSection() {
  const {
    t
  } = useLanguage();
  const PHRASES = [t('hero.phrase_1'), t('hero.phrase_2'), t('hero.phrase_3'), t('hero.phrase_4')];
  const LONGEST_PHRASE = t('hero.phrase_3'); // Using the longest one as placeholder
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const [displayedPhrase, setDisplayedPhrase] = useState(prefersReducedMotion ? PHRASES[0] : '');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(!prefersReducedMotion);
  const [markerKey, setMarkerKey] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Reset typing when language changes
  useEffect(() => {
    setDisplayedPhrase('');
    setPhraseIndex(0);
    setIsTyping(true);
  }, [t]);
  // Stagger reveal on mount
  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 60);
    return () => clearTimeout(t);
  }, []);
  // Typewriter engine
  useEffect(() => {
    if (prefersReducedMotion) return;
    const target = PHRASES[phraseIndex];
    if (isTyping) {
      if (displayedPhrase.length < target.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayedPhrase(target.slice(0, displayedPhrase.length + 1));
        }, randomBetween(TYPE_SPEED_MIN, TYPE_SPEED_MAX));
      } else {
        // Fully typed — pause then start erasing
        timeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, PAUSE_AFTER_TYPE);
      }
    } else {
      if (displayedPhrase.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayedPhrase(displayedPhrase.slice(0, -1));
        }, randomBetween(ERASE_SPEED_MIN, ERASE_SPEED_MAX));
      } else {
        // Fully erased — pause then move to next phrase
        timeoutRef.current = setTimeout(() => {
          const next = (phraseIndex + 1) % PHRASES.length;
          setPhraseIndex(next);
          setIsTyping(true);
          setMarkerKey(k => k + 1);
        }, PAUSE_AFTER_ERASE);
      }
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [displayedPhrase, isTyping, phraseIndex, prefersReducedMotion, PHRASES]);
  return <section className="relative flex items-center justify-center overflow-visible bg-gradient-to-b from-[#F8F5FF] via-[#FDFCFF] to-[#FAFAFA] pt-28 pb-10 lg:pt-32 lg:pb-14" data-id="element-161" data-mp="184vqd1ca0mem">
      {/* Animated background layer */}
      <div className="absolute inset-0 pointer-events-none hero-bg-animate" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(124,77,255,0.07) 0%, transparent 65%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(124,77,255,0.04) 0%, transparent 60%)'
    }} data-id="element-162" data-mp="2v1bruk38cb6s" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-163" data-mp="2760lg6xcye8s">
        {/* Headline — stagger slot 1 */}
        <h1 className={`text-[2.25rem] sm:text-[3rem] md:text-[3.5rem] lg:text-[4.25rem] font-bold text-[#1A1A2E] leading-[1.1] tracking-tight mb-4 hero-stagger-1 ${revealed ? 'hero-stagger-visible' : ''}`} data-id="element-164" data-mp="2g4ufpqn7usj6">
          {t('hero.title_prefix')}
          <br data-id="element-165" data-mp="3rjhssgv6xb3u" />
          <span className="relative inline-block align-baseline" data-id="element-166" data-mp="3rm3va23850f7">
            {/* Invisible width anchor — prevents layout shift */}
            <span aria-hidden="true" className="invisible select-none pointer-events-none" style={{
            display: 'inline-block',
            minWidth: '1ch'
          }} data-id="element-167" data-mp="3v1f6bmu8bsc9">
              {LONGEST_PHRASE}
            </span>

            {/* Animated highlight marker */}
            {displayedPhrase.length > 0 && <span key={markerKey} className="absolute bottom-0 left-0 h-[0.18em] rounded-full hero-marker-draw" style={{
            background: 'rgba(124,77,255,0.22)',
            width: '100%',
            bottom: '-0.05em'
          }} data-id="element-168" data-mp="hv5bllo96r7s" />}

            {/* Visible typed text — absolutely positioned over anchor */}
            <span className="absolute left-0 top-0 text-[#7C4DFF] whitespace-nowrap" style={{
            display: 'inline-flex',
            alignItems: 'baseline'
          }} data-id="element-169" data-mp="10l4yc1wmfu81">
              {prefersReducedMotion ? PHRASES[0] : displayedPhrase}
              {showCursor && !prefersReducedMotion && <span className="hero-cursor" aria-hidden="true" style={{
              display: 'inline-block',
              width: '0.06em',
              minWidth: 3,
              height: '0.85em',
              background: '#7C4DFF',
              marginLeft: '0.04em',
              verticalAlign: 'middle',
              borderRadius: 1,
              flexShrink: 0
            }} data-id="element-170" data-mp="1k5c4tolbycjs" />}
            </span>
          </span>
          <br data-id="element-171" data-mp="rjkbn0pqzhmr" />
          {t('hero.title_suffix')}
        </h1>

        {/* Subtitles — stagger slot 2 */}
        <div className={`mb-8 space-y-1 hero-stagger-2 ${revealed ? 'hero-stagger-visible' : ''}`} data-id="element-172" data-mp="dk1h5apv04sx">
          <p className="text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-173" data-mp="ik5ni7x1zj5f">
            {t('hero.subtitle_1')}
          </p>
          <p className="text-base sm:text-lg text-gray-600 font-semibold leading-relaxed" data-id="element-174" data-mp="2fga2mn3jefki">
            {t('hero.subtitle_2')}
          </p>
        </div>

        {/* CTA — stagger slot 3 */}
        <div className={`relative hero-stagger-3 ${revealed ? 'hero-stagger-visible' : ''}`} data-id="element-175" data-mp="26gdld6jh8izh">
          {/* CTA Glow blob */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" style={{
          width: 280,
          height: 100,
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(124,77,255,0.18) 0%, transparent 70%)',
          filter: 'blur(30px)'
        }} data-id="element-176" data-mp="335ull5zoxcts" />
          <a href="#solutions" data-id="element-177" data-mp="1drg2p2bcsk67">
            <Button size="lg" className="hero-cta text-base font-semibold tracking-widest uppercase px-14 py-4 rounded-2xl shadow-lg relative z-10" data-id="element-178" data-mp="1dqeq2teovpmd">
              {t('hero.cta')}
            </Button>
          </a>
        </div>

        {/* Scroll cue */}
        <div className={`mt-10 hero-stagger-3 ${revealed ? 'hero-stagger-visible' : ''}`} style={{
        transitionDelay: '500ms'
      }} data-id="element-179" data-mp="29tv7xrs4ie8">
          <a href="#solutions" className="inline-flex flex-col items-center gap-1 text-gray-400 hover:text-[#7C4DFF] transition-colors duration-300 group" data-id="element-180" data-mp="c05auhktsg0y">
            <span className="text-xs tracking-wide" data-id="element-181" data-mp="lycha6hhnfop">
              {t('hero.scroll_cue')}
            </span>
            <span className="text-sm hero-scroll-arrow" data-id="element-182" data-mp="q06fm847o7lp">↓</span>
          </a>
        </div>
      </div>

      {/* ── Gradient Arc Bridge ── */}
      <div className="absolute left-0 right-0 pointer-events-none" aria-hidden="true" style={{
      bottom: -120,
      height: 280,
      zIndex: 1
    }} data-id="element-183" data-mp="2nr0p6t52urat">
        {/* Main arc — blends light hero into #7D4EFF solutions */}
        <div style={{
        position: 'absolute',
        left: '-15%',
        width: '130%',
        height: '100%',
        borderRadius: '50%',
        background: 'radial-gradient(ellipse 100% 100% at 50% 0%, rgba(125,78,255,0.12) 0%, rgba(125,78,255,0.30) 50%, rgba(125,78,255,0.55) 80%, rgba(125,78,255,0.75) 100%)',
        filter: 'blur(20px)'
      }} data-id="element-184" data-mp="23umw6nh1485n" />
        {/* Glow accent */}
        <div style={{
        position: 'absolute',
        left: '15%',
        width: '70%',
        height: '50%',
        top: '5%',
        borderRadius: '50%',
        background: 'radial-gradient(ellipse 100% 100% at 50% 30%, rgba(124,77,255,0.10) 0%, transparent 70%)',
        filter: 'blur(28px)'
      }} data-id="element-185" data-mp="1ywld42jls8u6" />
      </div>
    </section>;
}